// src/padplanner/paperGridLayer.js
//
// Paper view's grid as a MapLibre custom layer: one WebGL draw of points and
// one of lines a frame, no DOM per dot. The numbers are paperGrid.js's.
//
// ── CACHED PER ZOOM LEVEL, BUILT OFF THE FRAME ─────────────────────────────
//
// The grid for the view plus half a view on each side is built once per
// whole zoom level (the tier is that level's, paperGrid.js), in a timeout
// after the frame that found it missing, never inside render(): a 5 ft
// lattice over a 1920 px window at zoom 19 is ~60,000 proj4 inverses, about
// 30 ms. Until it is built the old cache is drawn; it is geographic, so it is
// in the right place, only at the old density. A pan inside the cached area
// rebuilds nothing; the GPU redraws the same buffers with the new matrix.
//
// ── PRECISION ──────────────────────────────────────────────────────────────
//
// Vertices are uploaded relative to an origin (the cache's center), and the
// origin goes into the matrix, multiplied in doubles. Raw Web Mercator in
// float32 resolves only ~5 m on the ground, worse than the step.
import { gridTier, gridGeometry, metersPerPixel, mercator } from "./paperGrid";

export const PAPER_GRID_LAYER_ID = "pp-paper-grid";

const VS = `
attribute vec2 a_pos;
uniform mat4 u_matrix;
uniform float u_size;
void main() {
  gl_Position = u_matrix * vec4(a_pos, 0.0, 1.0);
  gl_PointSize = u_size;
}`;
const FS = `
precision mediump float;
uniform vec4 u_color;
uniform float u_round;
void main() {
  if (u_round > 0.5 && length(gl_PointCoord - vec2(0.5)) > 0.5) discard;
  gl_FragColor = u_color;
}`;

/** m · T(ox, oy): column-major, in doubles. */
export function withOrigin(m, ox, oy) {
  const out = new Float64Array(16);
  for (let i = 0; i < 16; i++) out[i] = m[i];
  for (let r = 0; r < 4; r++) out[12 + r] = m[r] * ox + m[4 + r] * oy + m[12 + r];
  return out;
}

/** A "#rrggbb" color (paperTheme.js) at an alpha, premultiplied as MapLibre blends. */
export function premultiplied(hex, alpha) {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(String(hex).slice(i, i + 2), 16));
  return [((r || 0) / 255) * alpha, ((g || 0) / 255) * alpha, ((b || 0) / 255) * alpha, alpha];
}

const contains = (outer, inner) => outer[0] <= inner[0] && outer[1] <= inner[1] && outer[2] >= inner[2] && outer[3] >= inner[3];

export class PaperGridLayer {
  /** @param {{onTier?: (tier: object|null) => void}} [opts] */
  constructor({ onTier = null } = {}) {
    this.id = PAPER_GRID_LAYER_ID;
    this.type = "custom";
    this.renderingMode = "2d";
    this.onTier = onTier;
    this.frame = null;
    this.stepFt = 5;
    this.enabled = false;
    this.colors = { dot: [0, 0, 0, 0.4], minor: [0, 0, 0, 0.12], major: [0, 0, 0, 0.25] };
    this.cache = null;
    this.pending = null;
    this.tierShown = undefined;
  }

  /** frame (frame.js) and step; a change drops the cache. */
  set({ frame = this.frame, stepFt = this.stepFt, enabled = this.enabled, colors = this.colors } = {}) {
    const moved = frame !== this.frame || stepFt !== this.stepFt;
    Object.assign(this, { frame, stepFt, enabled, colors });
    if (moved) this.cache = null;
    if (!enabled) this._tier(null);
    this.map?.triggerRepaint();
  }

  onAdd(map, gl) {
    this.map = map;
    const sh = (type, src) => { const s = gl.createShader(type); gl.shaderSource(s, src); gl.compileShader(s); return s; };
    const p = gl.createProgram();
    gl.attachShader(p, sh(gl.VERTEX_SHADER, VS));
    gl.attachShader(p, sh(gl.FRAGMENT_SHADER, FS));
    gl.linkProgram(p);
    this.gl = gl;
    this.program = p;
    this.loc = {
      pos: gl.getAttribLocation(p, "a_pos"), matrix: gl.getUniformLocation(p, "u_matrix"), size: gl.getUniformLocation(p, "u_size"),
      color: gl.getUniformLocation(p, "u_color"), round: gl.getUniformLocation(p, "u_round"),
    };
    this.buffers = { dots: gl.createBuffer(), minor: gl.createBuffer(), major: gl.createBuffer() };
  }

  onRemove(_map, gl) {
    clearTimeout(this.pending);
    for (const b of Object.values(this.buffers ?? {})) gl.deleteBuffer(b);
    if (this.program) gl.deleteProgram(this.program);
    this.map = null;
  }

  _tier(tier) {
    const key = tier ? JSON.stringify(tier) : null;
    if (key === this.tierShown) return;
    this.tierShown = key;
    this.onTier?.(tier);
  }

  /** The view's need: the zoom level and the box the cache must cover. */
  _view() {
    const b = this.map.getBounds();
    return { z: Math.floor(this.map.getZoom()), lat: this.map.getCenter().lat, bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] };
  }

  /** Build the cache for a view: synchronous, and off the frame (render schedules it). */
  build({ z, lat, bbox }) {
    const [w, s, e, n] = bbox;
    const dw = (e - w) / 2, dh = (n - s) / 2;
    const area = [w - dw, s - dh, e + dw, n + dh];
    const tier = gridTier({ stepFt: this.stepFt, mPerPx: metersPerPixel(z, lat) });
    const g = gridGeometry({ frame: this.frame, bbox: area, tier });
    const [ox, oy] = mercator([(w + e) / 2, (s + n) / 2]);
    const rel = (p) => { const [x, y] = mercator(p); return [x - ox, y - oy]; };
    const pairs = (lines) => {
      const out = [];
      for (const line of lines) for (let i = 1; i < line.length; i++) out.push(...rel(line[i - 1]), ...rel(line[i]));
      return new Float32Array(out);
    };
    const data = { dots: new Float32Array(g.dots.flatMap(rel)), minor: pairs(g.minor), major: pairs(g.major) };
    const gl = this.gl;
    if (gl) {
      for (const k of Object.keys(data)) {
        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers[k]);
        gl.bufferData(gl.ARRAY_BUFFER, data[k], gl.STATIC_DRAW);
      }
      gl.bindBuffer(gl.ARRAY_BUFFER, null);
    }
    this.cache = { z, stepFt: this.stepFt, frame: this.frame, bbox: area, origin: [ox, oy], counts: { dots: g.dots.length, minor: data.minor.length / 2, major: data.major.length / 2 }, tier };
    this._tier(tier);
    return this.cache;
  }

  _need() {
    if (!this.map || !this.frame) return null;
    const v = this._view();
    const c = this.cache;
    return c && c.z === v.z && c.stepFt === this.stepFt && c.frame === this.frame && contains(c.bbox, v.bbox) ? null : v;
  }

  render(gl, args) {
    if (!this.enabled || !this.frame) return;
    const need = this._need();
    if (need && this.pending == null) {
      this.pending = setTimeout(() => {
        this.pending = null;
        const now = this._need();
        if (now && this.enabled) { this.build(now); this.map?.triggerRepaint(); }
      }, 0);
    }
    const c = this.cache;
    if (!c) return;
    gl.useProgram(this.program);
    gl.uniformMatrix4fv(this.loc.matrix, false, new Float32Array(withOrigin(args.defaultProjectionData.mainMatrix, c.origin[0], c.origin[1])));
    gl.enableVertexAttribArray(this.loc.pos);
    const draw = (key, mode, count, color, size, round) => {
      if (!count) return;
      gl.bindBuffer(gl.ARRAY_BUFFER, this.buffers[key]);
      gl.vertexAttribPointer(this.loc.pos, 2, gl.FLOAT, false, 0, 0);
      gl.uniform4fv(this.loc.color, color);
      gl.uniform1f(this.loc.size, size);
      gl.uniform1f(this.loc.round, round ? 1 : 0);
      gl.drawArrays(mode, 0, count);
    };
    const dpr = window.devicePixelRatio || 1;
    draw("minor", gl.LINES, c.counts.minor, this.colors.minor, 1, false);
    draw("major", gl.LINES, c.counts.major, this.colors.major, 1, false);
    draw("dots", gl.POINTS, c.counts.dots, this.colors.dot, 2.5 * dpr, true);
    gl.bindBuffer(gl.ARRAY_BUFFER, null);
  }
}
